import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSnackbar } from 'notistack';
import RetentionListPage, { LIST_COLORS as C } from './RetentionListPage';
import { REPORTS_HEAD } from './retentionTableHeads';
import { getRetentionReportsList, remindParent } from '../../../redux/features/retentionSlice';

// /student-journey/reports — stage 8 first feedback + tutor check-in and the stage 9–11 reports, all students (§2.4).
// Open goes to the report's own stage; Remind parent shows on shared reports the parent hasn't opened.
export default function RetentionReports() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { reportsList, saving } = useSelector((state) => state.retention);
  const reloadRef = useRef(null);
  const onReady = useCallback((reload) => {
    reloadRef.current = reload;
  }, []);

  const headers = useMemo(() => {
    const remind = {
      field: 'remind',
      headerName: 'Parent',
      width: 150,
      renderCell: ({ row }) =>
        row.can_remind ? (
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            startIcon={<Icon icon="solar:bell-bing-bold" width={16} />}
            onClick={async (e) => {
              e.stopPropagation();
              const res = await dispatch(remindParent(row.report_id));
              const ok = res.payload?.status === 'success';
              enqueueSnackbar(res.payload?.message || (ok ? 'Reminder sent' : 'Could not send'), { variant: ok ? 'success' : 'error' });
              if (ok) reloadRef.current?.();
            }}
          >
            Remind
          </Button>
        ) : (
          '—'
        ),
    };
    const action = REPORTS_HEAD.length - 1;
    return [...REPORTS_HEAD.slice(0, action), remind, REPORTS_HEAD[action]];
  }, [dispatch, enqueueSnackbar, saving]);

  return (
    <RetentionListPage
      title="Reports"
      icon="solar:document-text-bold-duotone"
      headers={headers}
      fetchThunk={getRetentionReportsList}
      resource={reportsList}
      endpoint="GET /retention/reports"
      filterParam="report_type"
      openPath={(row) => `/student-journey/${row.course_enrollment_id}/tracking?stage=${row.stage_no}`}
      onReady={onReady}
      filters={[
        { label: 'All', value: '' },
        { label: '1st feedback', value: 'first_feedback' },
        { label: 'Tutor check-in', value: '4' },
        { label: 'Mid progress', value: '1' },
        { label: 'Feedback', value: '2' },
        { label: 'Comparison', value: '3' },
      ]}
      kpis={[
        { key: 'need_suggestions', label: 'Need suggestions', icon: 'solar:pen-new-square-bold-duotone', color: C.amber },
        { key: 'shared_not_viewed', label: 'Shared, not viewed', icon: 'solar:eye-closed-bold-duotone', color: C.red, hint: '48 h+' },
        { key: 'shared_this_month', label: 'Shared this month', icon: 'solar:share-bold-duotone', color: C.blue },
        { key: 'avg_improvement', label: 'Avg improvement', icon: 'solar:graph-up-bold-duotone', color: C.green, hint: 'Comparison reports' },
      ]}
    />
  );
}
